import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import { Sidebar } from '@/components/layout/sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { AppearancePanel } from '@/components/settings/appearance';
import { ConnectionsTab } from '@/components/settings/connections-tab';
import { TemplatesAdminTab } from '@/components/settings/templates-admin';
import { useCurrentUser, type Role } from '@/features/auth/use-current-user';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_auth/settings')({ component: SettingsPage });

interface ProfileForm {
  id: string;
  full_name: string;
  email: string;
  role: string;
  avatar_url: string;
}

function SettingsPage(): JSX.Element {
  const { data: me } = useCurrentUser();
  const isAdmin = me?.role === 'admin';

  return (
    <div className="flex h-screen bg-bg text-text">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="pl-14 pr-6 md:px-6 py-4 border-b border-border/[0.08]">
          <h1 className="font-display text-2xl">Settings</h1>
          <p className="text-text-dim text-sm mt-1">
            {isAdmin
              ? 'Profile, appearance, team — plus admin tools for assignments, users, and stats.'
              : 'Profile, appearance, and team.'}
          </p>
        </div>

        <div className="px-6 py-5 max-w-5xl">
          <Tabs defaultValue="profile">
            <TabsList className="border border-border/[0.08] bg-surface-2 p-1 h-auto flex-wrap">
              <TabTrigger value="profile">My Profile</TabTrigger>
              <TabTrigger value="appearance">Appearance</TabTrigger>
              <TabTrigger value="connections">Connections</TabTrigger>
              <TabTrigger value="team">Team</TabTrigger>
              {isAdmin && (
                <>
                  <TabTrigger value="templates">Templates</TabTrigger>
                  <TabTrigger value="assign">Lead Assignment</TabTrigger>
                  <TabTrigger value="users">User Management</TabTrigger>
                  <TabTrigger value="stats">System Stats</TabTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="profile" className="mt-5">
              <ProfileTab />
            </TabsContent>
            <TabsContent value="appearance" className="mt-5">
              <AppearancePanel />
            </TabsContent>
            <TabsContent value="connections" className="mt-5">
              <ConnectionsTab />
            </TabsContent>
            <TabsContent value="team" className="mt-5">
              <TeamTab />
            </TabsContent>
            {isAdmin && (
              <>
                <TabsContent value="templates" className="mt-5">
                  <TemplatesAdminTab />
                </TabsContent>
                <TabsContent value="assign" className="mt-5">
                  <LeadAssignmentTab />
                </TabsContent>
                <TabsContent value="users" className="mt-5">
                  <UserManagementTab />
                </TabsContent>
                <TabsContent value="stats" className="mt-5">
                  <SystemStatsTab />
                </TabsContent>
              </>
            )}
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function TabTrigger({ value, children }: { value: string; children: React.ReactNode }): JSX.Element {
  return (
    <TabsTrigger
      value={value}
      className="data-[state=active]:bg-accent/15 data-[state=active]:text-accent data-[state=active]:shadow-none text-text-dim hover:text-text px-3.5 py-1.5"
    >
      {children}
    </TabsTrigger>
  );
}

function ProfileTab(): JSX.Element {
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const onSignOut = async (): Promise<void> => {
    if (!window.confirm('Sign out of SoftSync-AI FLOW?')) return;
    setSigningOut(true);
    try {
      await supabase.auth.signOut();
    } finally {
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    void (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return;
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, role, avatar_url')
        .eq('id', u.user.id)
        .maybeSingle();
      const p = (data ?? {}) as { id?: string; full_name?: string; role?: string; avatar_url?: string };
      setForm({
        id: p.id ?? u.user.id,
        full_name: p.full_name ?? '',
        email: u.user.email ?? '',
        role: p.role ?? '—',
        avatar_url: p.avatar_url ?? '',
      });
    })();
  }, []);

  const onSave = async (): Promise<void> => {
    if (!form) return;
    setSaving(true);
    setMsg(null);
    const { error } = await (supabase.from('profiles') as unknown as {
      update: (v: Record<string, unknown>) => { eq: (k: string, v: string) => Promise<{ error: { message: string } | null }> };
    })
      .update({ full_name: form.full_name, avatar_url: form.avatar_url })
      .eq('id', form.id);
    setSaving(false);
    setMsg(error ? `Error: ${error.message}` : 'Saved.');
  };

  if (!form) return <p className="text-text-mute text-sm">Loading…</p>;

  return (
    <Card className="bg-surface border-border/[0.16]">
      <CardHeader>
        <CardTitle>My Profile</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
        </div>
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" value={form.email} disabled />
        </div>
        <div>
          <Label htmlFor="role">Role</Label>
          <Input id="role" value={form.role} disabled />
        </div>
        <div>
          <Label htmlFor="avatar">Avatar URL</Label>
          <Input
            id="avatar"
            value={form.avatar_url}
            placeholder="https://…"
            onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => void onSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {msg && <span className="text-sm text-text-dim">{msg}</span>}
        </div>

        <div className="mt-6 pt-5 border-t border-border/[0.08]">
          <p className="text-xs text-text-mute mb-2">Signed in as <span className="text-text-dim">{form.email}</span></p>
          <Button
            variant="outline"
            onClick={() => void onSignOut()}
            disabled={signingOut}
            className="text-danger border-danger/30 hover:bg-danger/10 hover:text-danger"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {signingOut ? 'Signing out…' : 'Log out'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface TeamMember {
  id: string;
  full_name: string | null;
  role: Role | null;
  is_active_agent: boolean | null;
  email: string | null;
}

async function fetchTeam(): Promise<TeamMember[]> {
  // profiles has no email; we read from a join via the auth.users metadata RPC, or fall back to per-row email lookup is impossible client-side.
  // Workaround: select profiles, then for each id we cannot read auth.users (no anon access). So we use the public.profiles_with_email view if it exists, else degrade to '—'.
  const { data: viewData } = await supabase
    .from('profiles_with_email')
    .select('id, full_name, role, is_active_agent, email');
  if (viewData && Array.isArray(viewData)) {
    return viewData as TeamMember[];
  }
  const { data } = await supabase
    .from('profiles')
    .select('id, full_name, role, is_active_agent')
    .order('full_name');
  return ((data ?? []) as Array<Omit<TeamMember, 'email'>>).map((p) => ({ ...p, email: null }));
}

function TeamTab(): JSX.Element {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const t = await fetchTeam();
      setMembers(t);
      setLoading(false);
    })();
  }, []);

  return (
    <Card className="bg-surface border-border/[0.16]">
      <CardHeader>
        <CardTitle>Team</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-text-mute text-sm">Loading…</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-text-mute text-xs uppercase tracking-wider border-b border-border/[0.08]">
                <th className="py-2">Name</th>
                <th className="py-2">Email</th>
                <th className="py-2">Role</th>
                <th className="py-2">Active</th>
              </tr>
            </thead>
            <tbody>
              {members.map((p) => (
                <tr key={p.id} className="border-b border-border/[0.04]">
                  <td className="py-2 font-medium">{p.full_name ?? '—'}</td>
                  <td className="py-2 text-text-dim">{p.email ?? '—'}</td>
                  <td className="py-2">
                    <span className="text-xs px-2 py-0.5 rounded bg-surface-2 border border-border/[0.08]">
                      {p.role ?? '—'}
                    </span>
                  </td>
                  <td className="py-2 text-text-dim">{p.is_active_agent ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

/* ─────────────────────── ADMIN — LEAD ASSIGNMENT ─────────────────────── */

interface AdminPoolRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  city: string | null;
  vertical: string | null;
  icp_score: number | null;
  pipeline: string | null;
}

function LeadAssignmentTab(): JSX.Element {
  const [pool, setPool] = useState<AdminPoolRow[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [target, setTarget] = useState<string>('');
  const [filter, setFilter] = useState<{ pipeline: string; vertical: string }>({ pipeline: 'softsync', vertical: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [total, setTotal] = useState(0);

  const loadPool = async (): Promise<void> => {
    let q = supabase
      .from('contacts')
      .select('id, first_name, last_name, company:company_name, city, vertical, icp_score, pipeline', { count: 'exact' })
      .is('assigned_to', null)
      .eq('pipeline', filter.pipeline);
    if (filter.vertical) q = q.eq('vertical', filter.vertical);
    q = q.order('icp_score', { ascending: false, nullsFirst: false }).range(page * pageSize, page * pageSize + pageSize - 1);
    const { data, count } = await q;
    setPool((data ?? []) as unknown as AdminPoolRow[]);
    setTotal(count ?? 0);
  };

  useEffect(() => {
    void (async () => {
      const t = await fetchTeam();
      setTeam(t.filter((x) => x.role === 'setter' || x.role === 'closer' || x.role === 'bdm'));
    })();
  }, []);

  useEffect(() => {
    void loadPool();
    setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.pipeline, filter.vertical, page]);

  const toggleSelect = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllOnPage = (): void => setSelected(new Set(pool.map((p) => p.id)));
  const clearSelected = (): void => setSelected(new Set());

  const assign = async (): Promise<void> => {
    if (!target || selected.size === 0) return;
    setBusy(true);
    setMsg(null);
    const ids = Array.from(selected);
    const { error } = await (supabase.from('contacts') as unknown as {
      update: (v: Record<string, unknown>) => { in: (k: string, v: string[]) => Promise<{ error: { message: string } | null }> };
    })
      .update({ assigned_to: target, visibility: 'team', in_pull_queue: false })
      .in('id', ids);
    setBusy(false);
    if (error) {
      setMsg(`Error: ${error.message}`);
      return;
    }
    setMsg(`Assigned ${ids.length} lead${ids.length === 1 ? '' : 's'} to ${team.find((t) => t.id === target)?.full_name ?? 'setter'}.`);
    setSelected(new Set());
    void loadPool();
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <Card className="bg-surface border-border/[0.16]">
        <CardHeader>
          <CardTitle>Admin Pool — unassigned leads</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 flex-wrap mb-4">
            <div>
              <Label className="text-xs text-text-mute">Pipeline</Label>
              <select
                value={filter.pipeline}
                onChange={(e) => {
                  setFilter((f) => ({ ...f, pipeline: e.target.value }));
                  setPage(0);
                }}
                className="block text-sm bg-surface-2 border border-border/[0.16] rounded px-3 py-1.5"
              >
                <option value="softsync">SoftSync</option>
                <option value="avara">Avara</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-text-mute">Vertical</Label>
              <select
                value={filter.vertical}
                onChange={(e) => {
                  setFilter((f) => ({ ...f, vertical: e.target.value }));
                  setPage(0);
                }}
                className="block text-sm bg-surface-2 border border-border/[0.16] rounded px-3 py-1.5"
              >
                <option value="">All</option>
                <option value="trades">Trades</option>
                <option value="local_services">Local Services</option>
                <option value="health_fitness">Health & Fitness</option>
                <option value="hospitality">Hospitality</option>
              </select>
            </div>
            <div className="flex-1" />
            <div className="text-xs text-text-mute font-mono px-2 py-1 rounded bg-surface-2">
              {total.toLocaleString()} unassigned · {selected.size} selected
            </div>
          </div>

          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="text-sm bg-surface-2 border border-border/[0.16] rounded px-3 py-1.5"
            >
              <option value="">Choose setter to assign…</option>
              {team.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.full_name ?? t.email ?? '—'} ({t.role})
                </option>
              ))}
            </select>
            <Button size="sm" onClick={() => void assign()} disabled={busy || !target || selected.size === 0}>
              {busy ? 'Assigning…' : `Assign ${selected.size}`}
            </Button>
            <Button size="sm" variant="outline" onClick={selectAllOnPage}>
              Select all on page
            </Button>
            <Button size="sm" variant="outline" onClick={clearSelected}>
              Clear
            </Button>
            {msg && <span className="text-sm text-text-dim ml-2">{msg}</span>}
          </div>

          <div className="border border-border/[0.08] rounded overflow-auto max-h-[480px]">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 sticky top-0">
                <tr className="text-left text-text-mute text-xs uppercase tracking-wider">
                  <th className="py-2 px-3 w-10"></th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Company</th>
                  <th className="py-2 pr-3">City</th>
                  <th className="py-2 pr-3">Vertical</th>
                  <th className="py-2 pr-3 text-right">ICP</th>
                </tr>
              </thead>
              <tbody>
                {pool.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-text-mute">
                      No unassigned leads in this filter.
                    </td>
                  </tr>
                ) : (
                  pool.map((c) => {
                    const isSel = selected.has(c.id);
                    return (
                      <tr
                        key={c.id}
                        onClick={() => toggleSelect(c.id)}
                        className={cn(
                          'cursor-pointer border-b border-border/[0.04] hover:bg-surface-2 transition',
                          isSel && 'bg-accent/10',
                        )}
                      >
                        <td className="py-2 px-3">
                          <input type="checkbox" checked={isSel} readOnly className="accent-cyan-400" />
                        </td>
                        <td className="py-2 pr-3">{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</td>
                        <td className="py-2 pr-3 text-text-dim">{c.company ?? '—'}</td>
                        <td className="py-2 pr-3 text-text-dim">{c.city ?? '—'}</td>
                        <td className="py-2 pr-3 text-text-dim">{c.vertical ?? '—'}</td>
                        <td className="py-2 pr-3 text-right font-mono text-xs">{c.icp_score ?? '—'}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className="text-text-mute">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ─────────────────────── ADMIN — USER MANAGEMENT ─────────────────────── */

const ROLES: Role[] = ['admin', 'closer', 'bdm', 'setter'];

function UserManagementTab(): JSX.Element {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    setLoading(true);
    const t = await fetchTeam();
    setMembers(t);
    setLoading(false);
  };

  useEffect(() => {
    void reload();
  }, []);

  const updateRole = async (id: string, role: Role): Promise<void> => {
    const { error } = await (supabase.from('profiles') as unknown as {
      update: (v: Record<string, unknown>) => { eq: (k: string, v: string) => Promise<{ error: { message: string } | null }> };
    })
      .update({ role })
      .eq('id', id);
    if (error) setMsg(`Error: ${error.message}`);
    else {
      setMsg('Role updated.');
      void reload();
    }
  };

  const toggleActive = async (id: string, next: boolean): Promise<void> => {
    const { error } = await (supabase.from('profiles') as unknown as {
      update: (v: Record<string, unknown>) => { eq: (k: string, v: string) => Promise<{ error: { message: string } | null }> };
    })
      .update({ is_active_agent: next })
      .eq('id', id);
    if (error) setMsg(`Error: ${error.message}`);
    else {
      setMsg(next ? 'Activated.' : 'Deactivated.');
      void reload();
    }
  };

  return (
    <Card className="bg-surface border-border/[0.16]">
      <CardHeader>
        <CardTitle>User Management</CardTitle>
        <p className="text-xs text-text-mute mt-1">
          Change role, activate/deactivate. New users invited via Supabase Auth — paste their email below.
        </p>
      </CardHeader>
      <CardContent>
        {msg && <div className="mb-3 text-sm text-text-dim">{msg}</div>}
        {loading ? (
          <p className="text-text-mute text-sm">Loading…</p>
        ) : (
          <div className="border border-border/[0.08] rounded overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2">
                <tr className="text-left text-text-mute text-xs uppercase tracking-wider">
                  <th className="py-2 px-3">Name</th>
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Role</th>
                  <th className="py-2 pr-3">Active</th>
                  <th className="py-2 pr-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-border/[0.04]">
                    <td className="py-2 px-3 font-medium">{m.full_name ?? '—'}</td>
                    <td className="py-2 pr-3 text-text-dim">{m.email ?? '—'}</td>
                    <td className="py-2 pr-3">
                      <select
                        value={m.role ?? 'setter'}
                        onChange={(e) => void updateRole(m.id, e.target.value as Role)}
                        className="text-xs bg-surface-2 border border-border/[0.16] rounded px-2 py-1"
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3 text-text-dim">{m.is_active_agent ? 'Yes' : 'No'}</td>
                    <td className="py-2 pr-3">
                      <Button size="sm" variant="outline" onClick={() => void toggleActive(m.id, !m.is_active_agent)}>
                        {m.is_active_agent ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 p-3 bg-surface-2 rounded border border-border/[0.08] text-xs text-text-dim">
          <strong className="text-text-dim">To add a new user:</strong> Supabase Dashboard → Authentication → Users → Invite. Once they accept, they'll appear here and you can set their role.
        </div>
      </CardContent>
    </Card>
  );
}

/* ─────────────────────── ADMIN — SYSTEM STATS ─────────────────────── */

interface SystemStats {
  totalContacts: number;
  softsyncContacts: number;
  avaraContacts: number;
  unassignedSoftsync: number;
  unassignedAvara: number;
  totalOpps: number;
  oppsBySetter: Array<{ name: string; count: number }>;
  contactsBySetter: Array<{ name: string; count: number }>;
  contactsByVertical: Array<{ vertical: string; count: number }>;
}

function SystemStatsTab(): JSX.Element {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      const team = await fetchTeam();
      const teamById = new Map(team.map((t) => [t.id, t.full_name ?? t.email ?? '—']));

      const [
        { count: totalContacts },
        { count: softsyncContacts },
        { count: avaraContacts },
        { count: unassignedSoftsync },
        { count: unassignedAvara },
        { count: totalOpps },
        { data: contactsByAssign },
        { data: oppsByAssign },
        { data: contactsByVerticalRows },
      ] = await Promise.all([
        supabase.from('contacts').select('id', { count: 'exact', head: true }),
        supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('pipeline', 'softsync'),
        supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('pipeline', 'avara'),
        supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('pipeline', 'softsync').is('assigned_to', null),
        supabase.from('contacts').select('id', { count: 'exact', head: true }).eq('pipeline', 'avara').is('assigned_to', null),
        supabase.from('opportunities').select('id', { count: 'exact', head: true }),
        supabase.from('contacts').select('assigned_to').not('assigned_to', 'is', null),
        supabase.from('opportunities').select('assigned_to').not('assigned_to', 'is', null),
        supabase.from('contacts').select('vertical').not('vertical', 'is', null),
      ]);

      const tally = (rows: { assigned_to: string | null }[] | null): Map<string, number> => {
        const m = new Map<string, number>();
        (rows ?? []).forEach((r) => {
          if (!r.assigned_to) return;
          m.set(r.assigned_to, (m.get(r.assigned_to) ?? 0) + 1);
        });
        return m;
      };

      const cByS = tally((contactsByAssign as { assigned_to: string | null }[] | null) ?? []);
      const oByS = tally((oppsByAssign as { assigned_to: string | null }[] | null) ?? []);

      const vertTally = new Map<string, number>();
      ((contactsByVerticalRows as { vertical: string | null }[] | null) ?? []).forEach((r) => {
        if (!r.vertical) return;
        vertTally.set(r.vertical, (vertTally.get(r.vertical) ?? 0) + 1);
      });

      setStats({
        totalContacts: totalContacts ?? 0,
        softsyncContacts: softsyncContacts ?? 0,
        avaraContacts: avaraContacts ?? 0,
        unassignedSoftsync: unassignedSoftsync ?? 0,
        unassignedAvara: unassignedAvara ?? 0,
        totalOpps: totalOpps ?? 0,
        contactsBySetter: Array.from(cByS, ([id, count]) => ({ name: teamById.get(id) ?? '—', count })).sort((a, b) => b.count - a.count),
        oppsBySetter: Array.from(oByS, ([id, count]) => ({ name: teamById.get(id) ?? '—', count })).sort((a, b) => b.count - a.count),
        contactsByVertical: Array.from(vertTally, ([vertical, count]) => ({ vertical, count })).sort((a, b) => b.count - a.count),
      });
      setLoading(false);
    })();
  }, []);

  if (loading) return <p className="text-text-mute text-sm">Loading…</p>;
  if (!stats) return <p className="text-text-mute text-sm">No data.</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <StatCard title="Contacts">
        <Stat label="Total" value={stats.totalContacts.toLocaleString()} />
        <Stat label="SoftSync" value={stats.softsyncContacts.toLocaleString()} />
        <Stat label="Avara" value={stats.avaraContacts.toLocaleString()} />
        <Stat label="Unassigned (SoftSync)" value={stats.unassignedSoftsync.toLocaleString()} accent={stats.unassignedSoftsync > 0} />
        <Stat label="Unassigned (Avara)" value={stats.unassignedAvara.toLocaleString()} accent={stats.unassignedAvara > 0} />
      </StatCard>

      <StatCard title="Opportunities">
        <Stat label="Total deals" value={stats.totalOpps.toLocaleString()} />
        {stats.oppsBySetter.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border/[0.08]">
            <p className="text-xs text-text-mute mb-2 uppercase tracking-wider">Deals by setter</p>
            {stats.oppsBySetter.map((s) => (
              <Stat key={s.name} label={s.name} value={s.count.toLocaleString()} />
            ))}
          </div>
        )}
      </StatCard>

      <StatCard title="Contacts by setter">
        {stats.contactsBySetter.length === 0 ? (
          <p className="text-xs text-text-mute">No assignments yet.</p>
        ) : (
          stats.contactsBySetter.map((s) => <Stat key={s.name} label={s.name} value={s.count.toLocaleString()} />)
        )}
      </StatCard>

      <StatCard title="Contacts by vertical">
        {stats.contactsByVertical.map((v) => (
          <Stat key={v.vertical} label={v.vertical} value={v.count.toLocaleString()} />
        ))}
      </StatCard>
    </div>
  );
}

function StatCard({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Card className="bg-surface border-border/[0.16]">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1.5 text-sm">{children}</CardContent>
    </Card>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-dim truncate">{label}</span>
      <span className={cn('font-mono', accent ? 'text-accent font-semibold' : 'text-text')}>{value}</span>
    </div>
  );
}
